import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AreaTitle from "../../components/areaTitle/AreaTitle";
import Loading from "../../components/loading/Loading";
import { DataProviderInstance } from "../../api/DataProvider";
import UpgradeModal from "../../components/subscription/UpgradeModal";
import ChatList from "./components/ChatList";
import ChatWindow from "./components/ChatWindow";
import useChats from "./hooks/useChats";
import useChatMessages from "./hooks/useChatMessages";
import { getChatKey, getSendTarget, isPlatformAdminSession } from "./utils";
import styles from "./WhatsappMessages.module.css";
import { useWhatsAppSender } from "../../contexts/WhatsAppSenderContext";

/**
 * Conversas de WhatsApp — orquestrador da tela.
 * A lógica de dados vive nos hooks (useChats, useChatMessages) e a
 * apresentação nos componentes (ChatList, ChatWindow, MessageBubble, Composer).
 */
function WhatsappMessages() {
  const {
    GetWhatsappChats,
    GetWhatsappChatMessages,
    SetWhatsappChatRead,
    SendWhatsappMessage,
    GetWhatsappAISuggestion,
    SetWhatsappAISuggestionOutcome,
    GetWhatsappConfig,
  } = DataProviderInstance();

  // Sugestão de resposta por IA só para administradores da plataforma (LGPD,
  // L-07): o recurso envia a conversa do paciente para o provedor de IA e
  // ainda não há garantia contratual de retenção zero. O backend aplica a
  // mesma restrição (whatsapp/suggest_reply.php). Para abrir às clínicas,
  // trocar esta condição e a do endpoint.
  const AI_SUGGESTION_ENABLED = isPlatformAdminSession();

  const [isWhatsappConfigured, setIsWhatsappConfigured] = useState(null); // null = carregando
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const navigate = useNavigate();
  const { capabilities, loading: senderLoading } = useWhatsAppSender();
  const pageRef = useRef(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [showChatList, setShowChatList] = useState(true);

  const {
    filteredChats,
    loading: loadingChats,
    searchText,
    setSearchText,
    filterOption,
    setFilterOption,
    markChatRead,
    updateChatPreview,
  } = useChats(GetWhatsappChats, isWhatsappConfigured === true);

  const {
    messages,
    loading: loadingMessages,
    loadingOlder,
    hasMore,
    loadOlder,
    sendMessage,
    retryMessage,
    discardMessage,
  } = useChatMessages({
    chat: selectedChat,
    GetWhatsappChatMessages,
    SendWhatsappMessage,
    SetWhatsappChatRead,
    onChatRead: markChatRead,
    onPreviewUpdate: updateChatPreview,
  });

  // Verifica se a integração está configurada
  useEffect(() => {
    GetWhatsappConfig((response) => {
      const configured = response && response.config &&
        response.config.instance_id &&
        response.config.has_instance_token;
      setIsWhatsappConfigured(!!configured);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isWhatsappConfigured === false) setShowUpgradeModal(true);
  }, [isWhatsappConfigured]);

  // Chat nunca é exibido no modo número da plataforma (só número próprio
  // conectado permite mensagens manuais) — acesso direto à rota redireciona
  // para a tela de configuração do WhatsApp
  useEffect(() => {
    if (!senderLoading && !capabilities.manual_chat) {
      navigate('/company/whatsapp', { replace: true });
    }
  }, [senderLoading, capabilities.manual_chat, navigate]);

  // Ajusta a altura da página para terminar exatamente no fim do viewport,
  // medindo onde ela começa (leva em conta topbar, breadcrumb e margens do
  // layout sem depender de números fixos). Assim o scroll fica só dentro
  // das colunas e o campo de digitação está sempre visível.
  useLayoutEffect(() => {
    const fitHeight = () => {
      const el = pageRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      // Ainda não renderizado (ex.: transição de rota): tenta no próximo frame
      if (rect.width === 0) {
        requestAnimationFrame(fitHeight);
        return;
      }

      // Topo ABSOLUTO no documento (posição atual + scroll): imune ao
      // estado do scroll no momento da medição
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const absoluteTop = rect.top + scrollY;

      // clientHeight em vez de innerHeight: desconta a barra de rolagem
      // horizontal do navegador
      const viewportHeight = document.documentElement.clientHeight;
      const bottomGap = 24; // respiro inferior
      const height = Math.max(viewportHeight - absoluteTop - bottomGap, 420);

      // flex:none neutraliza o "flex: 1" do .page-content global — com
      // flex-basis 0% a altura definida seria ignorada e a página
      // esticaria até o tamanho do conteúdo (scroll no body inteiro)
      el.style.flex = 'none';
      el.style.height = height + 'px';
      el.style.maxHeight = height + 'px';
      window.scrollTo(0, 0);
    };

    fitHeight();
    // Re-mede depois que transições/fontes/header assentam
    const timers = [
      setTimeout(fitHeight, 250),
      setTimeout(fitHeight, 800),
    ];
    window.addEventListener('resize', fitHeight);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', fitHeight);
    };
  }, [isWhatsappConfigured]);

  // Alterna entre layout desktop (duas colunas) e mobile (colunas deslizantes)
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);
      if (!mobile) setShowChatList(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    if (isMobileView) setShowChatList(false);
  };

  // Sugestão de resposta em modo copiloto: o servidor lê o histórico real da
  // conversa pela chave, junta o contexto da clínica, do contato e da agenda
  // e devolve a resposta que a atendente pode enviar, editar ou descartar.
  const handleRequestAISuggestion = (done) => {
    if (!selectedChat) {
      done(null, 'Selecione uma conversa para gerar uma sugestão.');
      return;
    }

    GetWhatsappAISuggestion(getChatKey(selectedChat), getSendTarget(selectedChat), (result) => {
      if (result && result.success && result.suggestion && result.suggestion.reply) {
        done(result.suggestion);
      } else {
        done(null, result?.error || null);
      }
    });
  };

  // Registro do que a atendente fez com a sugestão (enviou, editou, descartou)
  const handleSuggestionOutcome = (suggestionId, outcome, sentText) => {
    if (!suggestionId) return;
    SetWhatsappAISuggestionOutcome(suggestionId, outcome, sentText || '');
  };

  return (
    <div ref={pageRef} className={`page-content ${styles.page}`}>
      <div className={styles.pageHeader}>
        <AreaTitle
          Title="Conversas WhatsApp"
          Icon={<WhatsAppIcon />}
          Description="Leia e responda as mensagens de WhatsApp dos seus pacientes."
        />
      </div>

      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="WhatsApp não configurado"
        message="Para usar as mensagens do WhatsApp, configure sua instância em Integrações > WhatsApp."
        benefits={[
          "Envie e receba mensagens de WhatsApp diretamente da plataforma",
          "Mantenha todo o histórico de conversas com seus pacientes",
          "Use a IA para sugerir respostas rápidas",
          "Melhore a comunicação com seus pacientes"
        ]}
        ctaText="Configurar WhatsApp"
        ctaLink="/company/whatsapp"
        hideCloseButton={true}
      />

      {isWhatsappConfigured === null && (
        <div className={styles.pageLoading}>
          <Loading />
        </div>
      )}

      {isWhatsappConfigured === true && (
        <div className={styles.container}>
          <div className={`${styles.chatList} ${!showChatList && isMobileView ? styles.chatListHidden : ''}`}>
            <ChatList
              chats={filteredChats}
              loading={loadingChats}
              selectedKey={selectedChat ? getChatKey(selectedChat) : null}
              onSelect={handleSelectChat}
              searchText={searchText}
              onSearchChange={setSearchText}
              filterOption={filterOption}
              onFilterChange={setFilterOption}
            />
          </div>

          <div className={`${styles.chatArea} ${showChatList && isMobileView ? styles.chatAreaHidden : ''}`}>
            <ChatWindow
              chat={selectedChat}
              messages={messages}
              loading={loadingMessages}
              loadingOlder={loadingOlder}
              hasMore={hasMore}
              onLoadOlder={loadOlder}
              onSend={sendMessage}
              onRetry={retryMessage}
              onDiscard={discardMessage}
              onRequestAISuggestion={AI_SUGGESTION_ENABLED ? handleRequestAISuggestion : undefined}
              onSuggestionOutcome={AI_SUGGESTION_ENABLED ? handleSuggestionOutcome : undefined}
              isMobileView={isMobileView}
              onBack={() => setShowChatList(true)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsappMessages;
